import React, { useEffect, useState } from "react";
import BreachesTab from "../BreachesTab";
import BreachHeaderText from "../BreachHeaderText";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import * as ConfigurationActions from "../../../store/configurationService/actions";
import InfoIcon from "../../../Assets/images/icon-info-circle.svg";
import { DialogComponent } from "../../common/DialogComponent";
import SafetyScoreInfo from "./SafetyScoreInfo";
import { getBreachScoreFactors } from "../../../store/service";
import { Notification } from "../../common/Notification";
import SafetyScoreGraphSvg from "./SafetyScoreGraphSvg";
import { breachSafetyScoreBetween } from "./../../../util/CommonBreach";
import { breachScore } from "../BreachConstants";

const apiFailText = {
    na: "Not Available",
};

interface ISafetyScore {
    normalizedScore: number;
    numberOfActionsCompleted: number;
    monthsSinceMostRecentBreach: number;
    totalNumberOfBreaches: number;
    topIdentityRisks: {
        id: number;
        name: string;
        popup: string;
        value: number;
    }[];
    totalActions: number;
}

function SafetyScore() {
    const [isSafetyDialogOpen, setisSafetyDialogOpen] = useState(false);
    const [data, setData] = useState<ISafetyScore>();
    const [isLoading, setIsLoading] = useState(false);
    const [value, setValue] = useState<any>();
    const routeLoading = useSelector((state: ApplicationState) => state.configurationState && (state.configurationState.loading || false));
    const dispatch = useDispatch();
    const isNoScore = data && data?.normalizedScore === 100 && data.totalNumberOfBreaches === 0;
    const closeDialog = () => {
        setisSafetyDialogOpen(false);
    };

    const handleKeyDown = (e: { keyCode: number }) => {
        if (e.keyCode === 13) {
            setisSafetyDialogOpen(true);
        }
    };
    useEffect(() => {
        setIsLoading(true);
        getBreachScoreFactors()
            .then((res) => {
                setIsLoading(false);
                if (res.isSuccess) {
                    setData(res.body);
                    setTimeout(() => {
                        setValue(res.body.normalizedScore);
                    }, 1200);
                } else {
                    setValue("NA");
                    Notification({
                        type: "error",
                        title: "ID Safety Score",
                        message:
                            "We are currently experiencing difficulties in displaying your ID Safety Score and related factors. Please try again at a later time.",
                    });
                }
            })
            .catch((err) => {
                setIsLoading(false);
                setValue("NA");
                Notification({
                    type: "error",
                    title: "ID Safety Score",
                    message: "We are currently experiencing difficulties in displaying your ID Safety Score and related factors. Please try again at a later time.",
                });
            });
    }, []);

    useEffect(() => {
        if (routeLoading) {
            dispatch(ConfigurationActions.actionCreators.setRouteLoading(false));
        }
    }, [dispatch, routeLoading]);

    const renderGraph = () => {
        return <SafetyScoreGraphSvg isLoading={isLoading} value={value} />;
    };
    const renderNoScore = () => {
        return (
            <div className="box-description null-body-detected-breach">
                <div className="row row-container">
                    <div className="col-sm-3"></div>
                    <div className="col-xs-12 col-sm-6 text-align-center col-main">
                        <div className="icon-main">
                            <em className="las la-check-circle complete-icon"></em>
                        </div>
                        <div>
                            <div className="title-main">You do not have any detected breach exposures.</div>
                            <div className="description">
                                BreachIQ is continuously monitoring your data. We will notify you if we detect your data has been exposed in a breach.
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-3"></div>
                </div>
            </div>
        );
    };
    const renderHeading = () => (
        <div className="box">
            <div className="box-header">
                <div className="box-title-container">
                    <div className="box-title-text">
                        <div className="flex align-items-center">
                            <h2 className="breach-section-title">ID Safety Score</h2>
                            <span className="padding-left-xs" role="button" onClick={() => setisSafetyDialogOpen(true)} tabIndex={0} onKeyDown={handleKeyDown}>
                                <img
                                    src={InfoIcon}
                                    className="pointer"
                                    style={{ width: "1.5rem" }}
                                    alt="Circle with the letter 'i' in the center, signifying information"
                                />
                            </span>
                        </div>
                        <span className="f-12 bold">Powered by BreachIQ</span>
                    </div>
                </div>
            </div>
            {isNoScore ? renderNoScore() : <div className="box-content">{renderGraph()}</div>}
        </div>
    );

    return (
        <>
            <div className={isLoading ? "row loading" : "row"}>
                <div className={isNoScore ? "col-xs-12" : data ? "col-xs-12 col-sm-8" : "col-xs-8"}>
                    {renderHeading()}
                    {data && !isNoScore && (
                        <div className="box">
                            <div className="box-content">
                                <p className="f-19">
                                    Your identity is in the{" "}
                                    <span className="fw-700 text-lowercase">{breachScore[breachSafetyScoreBetween(data.normalizedScore)]["text"]}</span> category
                                    with a score of <span className="fw-700">{data.normalizedScore}</span> out of <span className="fw-700">100</span>.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                {!isLoading && !isNoScore && (
                    <div className="col-xs-12 col-sm-4">
                        <div className="box">
                            <div className="box-title-container">
                                <div className="box-title-text">
                                    <div className="box-title-container">
                                        <h2 className="breach-section-title">Factors impacting your ID Safety Score</h2>
                                    </div>
                                </div>
                            </div>

                            <div className="box-content">
                                <div className="breach-card">
                                    <div className="title">Data Breaches</div>
                                    {data ? (
                                        <div className="description">
                                            <span className="count">{data.totalNumberOfBreaches}</span>
                                            <span>total breaches</span>
                                        </div>
                                    ) : (
                                        <div className="description">{apiFailText.na}</div>
                                    )}
                                </div>
                                <div className="breach-card">
                                    <div className="title">Top Risks</div>
                                    {data && data.topIdentityRisks && data.topIdentityRisks.length > 0 ? (
                                        <ul className="list">
                                            {data.topIdentityRisks.map((item) => (
                                                <li key={item.id}>{item.name}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="description">{apiFailText.na}</div>
                                    )}
                                </div>
                                <div className="breach-card">
                                    <div className="title">Action History</div>
                                    {data ? (
                                        <div className="description">
                                            <span className="count">{`${data.numberOfActionsCompleted} of ${data.totalActions}`}</span>
                                            <span>completed actions</span>
                                        </div>
                                    ) : (
                                        <div className="description">{apiFailText.na}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="risk-rating-dialog">
                {isSafetyDialogOpen && (
                    <DialogComponent dialogId="dialog-risk-rating-info" title="Breach Risk Rating Info" closeDialog={closeDialog}>
                        <SafetyScoreInfo closeDialog={closeDialog} />
                    </DialogComponent>
                )}
            </div>
        </>
    );
}

export default SafetyScore;
